import React from 'react';
import { cn } from './cn';
import type { PositionRow } from '../types';
import { isWhiteLabel } from '../config/tenantConfig';
import { getStatusVariant, type StatusVariant } from './statusColors';
import { chainChildSource, getEntrySourceVariant, ENTRY_SOURCE_FILLED_CLASSES } from './entrySource';
import { istParts } from './dateUtils';

export const TH =
  'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider';

/**
 * Human-readable label for the per-position missed-exit chip / message.
 * Shared across PositionTableView (inline chip under symbol) and
 * StrategyPositionsModal (inline error message in the Exit cell) so the
 * wording stays in lock-step.
 *
 * Reasons mirror the backend `_compute_position_missed_exit` taxonomy:
 *   - `past_exit`     — strategy's scheduled exit moment has passed and the
 *                       position is still OPEN. Operator must exit manually.
 *   - `stuck_pending` — EXIT_PENDING longer than STUCK_EXIT_PENDING_MINUTES.
 *                       Broker order is stuck; operator must intervene.
 */
export function missedExitLabel(m: { reason: string; stuck_minutes?: number }): string {
  if (m.reason === 'past_exit') {
    return 'Exit missed — past scheduled exit time';
  }
  if (m.reason === 'stuck_pending') {
    const mins = m.stuck_minutes;
    return mins != null
      ? `Exit stuck pending — ${mins} min at broker`
      : 'Exit stuck pending — broker has not filled';
  }
  return 'Exit missed';
}

/**
 * Status → Badge variant mapping. Delegates to the canonical `getStatusVariant`
 * so the same status value renders the same colour on every screen.
 * Returns a Proxy-like object so callers can keep using `statusBadgeVariant[status]`.
 */
const VARIANT_TO_BADGE: Record<StatusVariant, 'default' | 'success' | 'warning' | 'danger' | 'info' | 'slate'> = {
  green:   'success',
  yellow:  'warning',
  red:     'danger',
  gray:    'default',
  blue:    'info',
  // PENDING_PLACE (claim in-flight) maps to Badge's `default` grey — matches
  // the CHIP_DENSE.PENDING_PLACE neutral hue for transient in-flight state.
  placing: 'default',
  // TERMINATED (platform stopped watching) — distinct slate Badge (Badge now
  // has a 'slate' variant) so it never reads as gray/CLOSED on the positions
  // table + modals, which render status via <Badge>.
  slate:   'slate',
};
export const statusBadgeVariant = new Proxy(
  {} as Record<string, 'info' | 'default' | 'warning' | 'danger' | 'success' | 'slate'>,
  {
    get: (_t, key: string) => {
      // getDisplayStatus emits "EXIT PENDING" / "TRIGGER PENDING" / "ENTRY PENDING"
      // (space-separated) — normalize to underscore form before lookup.
      const normalized = key.replace(/ /g, '_');
      return VARIANT_TO_BADGE[getStatusVariant(normalized)];
    },
  },
);

export function getDisplayStatus(p: PositionRow): string {
  if (p.entryStatus === 'REJECTED') return 'REJECTED';
  // Synthesized schedule placeholder — no real status to show.
  if (!p.status) return '';
  if (p.status === 'ENTRY_PENDING') {
    // WAT / ORB legs that are still waiting for the trigger read nicer as "TRIGGER PENDING"
    if (p.entryStatus === 'TRIGGER_PENDING') return 'TRIGGER PENDING';
    // PENDING_PLACE (Phase 6, claim-then-place in flight ~1-3s) — surface as
    // 'PLACING' so the row's Status cell matches getStatusLabel's 'Placing…'
    // rendering on adjacent surfaces. Without this branch the cell would
    // fall through to 'ENTRY PENDING' (yellow) and contradict the neutral
    // grey chip getStatusVariant emits on the same value.
    if (p.entryStatus === 'PENDING_PLACE') return 'PLACING';
    return 'ENTRY PENDING';
  }
  if (p.status === 'EXIT_PENDING') return 'EXITING';
  return p.status;
}

// Chain-depth badge: small round chip showing the position's index in its
// re-spawn chain. sequenceNo is chain-monotonic — increments on every spawn
// regardless of feature (SL_REENTRY / SL_RECOST / SL_REEXEC / TP_REEXEC), so
// this is NOT a per-feature attempt count. The EntrySourceBadge cluster shows
// the per-feature counters; this chip just signals "depth N in the chain".
// sequenceNo = 0 is the chain root — no badge.
//
// Colour: chosen by the chain spawn source — chainChildSource(entry_source,
// reEntryConfig.type): the row's own entry_source, except a UL re-wait child
// (entry_source=WAIT_AND_TRADE) takes its re-entry type — blue for SL
// re-entry, indigo for SL re-cost, cyan for SL re-exec, emerald for TP
// re-exec. Defaults to blue for legacy / unrecognised sources.

// Domain-specific labels for the chain-depth tooltip — distinct from the
// shorter budget-chip labels in `getReEntryFlavourMeta` (which says "Re-Entry"
// / "Re-Cost" / "Re-Exec" to fit the N/M chip). Here the tooltip reads
// "Chain position 3 · TP re-execute · …" — fuller-form for human reading.
const RE_SEQUENCE_LABEL_BY_SOURCE: Record<string, string> = {
  SL_REENTRY: 'SL re-entry',
  RE_ENTRY:   'SL re-entry',
  SL_RECOST:  'SL re-cost',
  RE_COST:    'SL re-cost',
  SL_REEXEC:  'SL re-execute',
  TP_REEXEC:  'TP re-execute',
};

function reSequenceChipMeta(entrySource: string | null | undefined): { chip: string; label: string } {
  // Colour goes through the shared helper so it can't drift from the
  // EntrySourceBadge cluster's hue for the same source.
  const variant = getEntrySourceVariant(entrySource);
  const chip = ENTRY_SOURCE_FILLED_CLASSES[variant];
  const label = (entrySource && RE_SEQUENCE_LABEL_BY_SOURCE[entrySource]) || 'chain spawn';
  return { chip, label };
}

export const ReSequenceBadge: React.FC<{ p: PositionRow; className?: string }> = ({ p, className }) => {
  const n = p.sequenceNo ?? 0;
  if (n <= 0) return null;
  const meta = reSequenceChipMeta(chainChildSource(p.entrySource, p.reEntryConfig?.type));
  const tooltip = p.parentPositionId
    ? `Chain position ${n} · ${meta.label} · Parent: ${p.parentPositionId}`
    : `Chain position ${n} · ${meta.label}`;
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center w-4 h-4 rounded-full',
        meta.chip,
        'text-[10px] font-semibold tabular-nums shrink-0',
        className,
      )}
      title={tooltip}
      aria-label={`Chain position ${n}, ${meta.label}`}
    >
      {n}
    </span>
  );
};

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const { yyyy, mm, dd, hh, min, ss } = istParts(iso);
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  } catch {
    return iso;
  }
}

export function formatTimeShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const { hh, min, ss } = istParts(iso);
    return `${hh}:${min}:${ss}`;
  } catch {
    return iso;
  }
}

/**
 * Compact datetime for narrow cards: `MM-DD HH:MM:SS` (14 chars).
 * Used in the Strategy leg card where a full `YYYY-MM-DD HH:MM:SS` would
 * overflow the 3-column detail grid.
 */
export function formatDateTimeCompact(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const { mm, dd, hh, min, ss } = istParts(iso);
    return `${mm}-${dd} ${hh}:${min}:${ss}`;
  } catch {
    return iso;
  }
}

export function formatNow(): string {
  const { hh, min, ss } = istParts(new Date().toISOString());
  return `${hh}:${min}:${ss}`;
}

/**
 * Canonical P&L formatting across the app: signed integer, no ₹ symbol, en-IN
 * grouping (e.g. `+5,937`, `-750`). Paisa-level precision is intentionally
 * dropped — at the volumes traders track at the strategy/basket level the
 * decimals are noise and break scanning density.
 */
export function formatPnlValue(pnl: number): string {
  const prefix = pnl >= 0 ? '+' : '';
  return `${prefix}${Math.round(pnl).toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Returns formatted P&L `text` + matching Tailwind `className` so a caller can
 * render them in custom markup. Use `formatPnl` (React node) for simple cases.
 */
export function formatPnlParts(
  pnl: number | null | undefined,
): { text: string; className: string } {
  if (pnl === null || pnl === undefined) {
    return { text: '—', className: 'text-gray-400 dark:text-gray-500' };
  }
  return {
    text: formatPnlValue(pnl),
    className: pnl >= 0
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-500 dark:text-red-400',
  };
}

export function formatPnl(pnl: number | null | undefined): React.ReactNode {
  const { text, className } = formatPnlParts(pnl);
  return <span className={cn(className, 'font-semibold')}>{text}</span>;
}

export function formatPrice(price: number | null | undefined): string {
  if (price == null || price === 0) return '—';
  return price.toFixed(2);
}

/** "650 (10)" when lots is known and > 0, else just "650". */
export function formatQtyWithLots(qty: number, lots?: number | null): string {
  return lots && lots > 0 ? `${qty} (${lots})` : `${qty}`;
}

export const sideColor = (side: string) => {
  if (side !== 'BUY') return 'text-red-500 dark:text-red-400';
  return isWhiteLabel() ? 'text-zebu-600 dark:text-zebu-400' : 'text-blue-600 dark:text-blue-400';
};

export const isCancellable = (p: PositionRow) =>
  p.status === 'ENTRY_PENDING' && p.entryStatus !== 'REJECTED';

/**
 * Parse an ISO string into { time, date } for stacked two-line display.
 * Line 1: `HH:MM:SS` (primary). Line 2: `MM-DD` (secondary).
 * Returns null parts when input is absent.
 */
export function splitTimeDate(iso: string | null | undefined): { time: string; date: string } | null {
  if (!iso) return null;
  try {
    const { mm, dd, hh, min, ss } = istParts(iso);
    return { time: `${hh}:${min}:${ss}`, date: `${mm}-${dd}` };
  } catch {
    return { time: iso, date: '' };
  }
}

/**
 * Leg sort order for StrategyPositionsModal — latest entry first.
 * Primary key: entry timestamp (ISO lex-sortable). Tie-breakers: leg index
 * ascending, then sequence_no descending (latest re-entry child before parent).
 *
 * Lives here (pure function, no component deps) so tests can import it
 * without pulling StrategyPositionsModal's transitive component chain through
 * Jest's module system.
 */
export function compareLegPositions(a: PositionRow, b: PositionRow): number {
  const aIso = a.entryFilledAt || a.entryTime || '';
  const bIso = b.entryFilledAt || b.entryTime || '';
  if (aIso && bIso) {
    const cmp = bIso.localeCompare(aIso);
    if (cmp !== 0) return cmp;
  } else if (aIso !== bIso) {
    return aIso ? -1 : 1;
  }
  const legDiff = (a.legIndex ?? 0) - (b.legIndex ?? 0);
  if (legDiff !== 0) return legDiff;
  return (b.sequenceNo ?? 0) - (a.sequenceNo ?? 0);
}
